import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    useGetDisputeReasonsQuery,
    useGetResolutionReasonsQuery,
    useResolveDisputeMutation,
} from '../../features/disputes/disputeSlice';
import { selectCurrentEmpid, selectCurrentUser } from '../../pages/login/authSlice';
import { ConstReasonType } from '../../utils/typeConstants';
import { useCreateScorecardDisputeMutation } from './addScorecardDisputeSlice';
import DescriptionBox from '../descriptionBox/DescriptionBox';

export type AddScorecardDisueProps = {
    /**
     * Scorecard version
     */
    readonly scorecardVersion?: number;
    /**
     * Add dispute dialog enable
     */
    readonly addDisputeDialogEnable: boolean;
    /**
     * Optional click handler
     */
    readonly closeAddDisputes: (value: boolean) => void;
    /**
     * Disable dispute details
     */
    readonly disableDisputeDetails: boolean;
    /**
     * Selected disputes list
     */
    readonly selectedDisputesList: any;
    /**
     * Selected dispute row data
     */
    readonly selectedDisputeRowData: any;
    /**
     * De fine the Mode "add" or "resolve"
     */
    readonly mode: 'add' | 'resolve';
    /**
     * Is manager window open "In case the windows approval started"
     */
    readonly isMangerWindowOpen?: boolean;
};
// eslint-disable-next-line max-statements
export const AddScorecardDispute = ({
    scorecardVersion,
    addDisputeDialogEnable,
    closeAddDisputes,
    disableDisputeDetails,
    selectedDisputesList,
    selectedDisputeRowData,
    mode,
    isMangerWindowOpen,
}: AddScorecardDisueProps) => {
    const [CreateDispute] = useCreateScorecardDisputeMutation();
    const [ResolveDispute] = useResolveDisputeMutation();
    const loggedUser = useSelector(selectCurrentUser);
    const loggedEmpNum = useSelector(selectCurrentEmpid);

    const [disputeDate, setDisputeDate] = useState(new Date());
    const [minDate, setMinDate] = useState(new Date());
    const [maxDate, setMaxDate] = useState(new Date());
    const [disputeCreateDate, setDisputeCreateDate] = useState(new Date());
    const [disputeReason, setDisputeReason] = useState<number>(0);
    const [disputeComments, setDisputeComment] = useState('');
    const [disputeCreatedBy, setDisputeCreatedBy] = useState(loggedUser);
    const [disputeDialog, setDisputeDialog] = useState(false);
    const [resolutionReason, setResolutionReason] = useState('');
    const [resolutionCommentText, setResolutionCommentText] = useState('');
    const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
    const [enableSaveButton, setEnableSaveButton] = useState(false);

    useEffect(() => {
        const selectedDate = new Date(window.localStorage.TLDate);
        const month = selectedDate.getMonth();
        const year = selectedDate.getFullYear();
        const nextMonth = month === 11 ? 0 : month + 1;
        setMinDate(new Date(year, month, 1));
        setMaxDate(new Date(year, nextMonth, 0));
        if (month === new Date().getMonth() && year === new Date().getFullYear()) {
            setMaxDate(new Date(year, month, new Date().getDate()));
        }
        setDisputeDate(new Date(year, month, 1));
    }, [window.localStorage.TLDate]);
    const { data: disputeReasonData } = useGetDisputeReasonsQuery({
        typeId: ConstReasonType.SCORECARD_DISPUTE_REASON,
    });
    const { data: resolutionReasons } = useGetResolutionReasonsQuery({
        typeId: ConstReasonType.SCORECARD_RESOLUTION_REASON,
    });
    useEffect(() => {
        setDisputeDialog(addDisputeDialogEnable);
        if (selectedDisputesList?.length === 1 && selectedDisputeRowData && mode === 'resolve') {
            setDisputeComment(selectedDisputeRowData?.disputeComment);
            setDisputeReason(selectedDisputeRowData?.dispute_reason_id);
            setDisputeCreatedBy(selectedDisputeRowData?.disputeCreateBy);
            setDisputeDate(new Date(selectedDisputeRowData?.dateofDispute));
            setDisputeCreateDate(new Date(selectedDisputeRowData?.dateAdded));
        }
        if (mode === 'add' && saveSuccessMessage === '') {
            clearData();
        }
    }, [addDisputeDialogEnable, selectedDisputesList, selectedDisputeRowData, mode]);
    const createScorecardDispute = (disputes: any) => {
        if (!isMangerWindowOpen) {
            CreateDispute(disputes)
            .unwrap()
            .then(() => {
                setSaveSuccessMessage('Added Successfully');
            })
            .then((error: any) => {
                if (error) console.log(error);
            });
        } else toast.info('Sorry, you cannot create a dispute once the approval window has started.');
    };
    const disputeModalHeader = () => {
        return (
            <h5 className="pe-3">
                <span style={{ fontSize: '16px', fontWeight: 700 }} className="pt-2">
                    {disableDisputeDetails ? 'Resolve Dispute' : 'Add Dispute'}
                </span>
            </h5>
        );
    };
    const dialogFuncMap = {
        disputeDialog: setDisputeDialog,
    };
    const clearData = () => {
        setResolutionReason('');
        setResolutionCommentText('');
        setDisputeComment('');
        setDisputeReason(0);
        setDisputeDate(minDate);
        setDisputeCreateDate(new Date());
        setDisputeCreatedBy(loggedUser);
        setSaveSuccessMessage('');
    };
    const hideDisputeDialog = () => {
        dialogFuncMap.disputeDialog(false);
        closeAddDisputes(false);
        clearData();
    };
    const DateOnChange = (e: any) => {
        setDisputeDate(e.value);
    };
    const saveDispute = () => {
        if (mode === 'add') {
            window.localStorage.saveDispute = true;
            const disputes = {
                disputeReasonid: disputeReason,
                disputeComment: disputeComments,
                supervisorNumber: loggedEmpNum,
                disputeCreateBy: loggedEmpNum,
                scorecardVersionId: scorecardVersion,
                disputeDate: `${disputeDate.getFullYear()}-${
                    disputeDate.getMonth() + 1
                }-${disputeDate.getDate()}`,
            };
            createScorecardDispute(disputes);
        } else if (mode === 'resolve') {
            const disputes = [];
            for (let i = 0; i < selectedDisputesList.length; i++) {
                const dispute = {
                    disputeId: selectedDisputesList[i],
                    disputeTypeId: 2,
                    disputeStatusId: 2,
                    resolutionReasonId: resolutionReason,
                    resolutionComment: resolutionCommentText,
                    resolvedBy: loggedEmpNum,
                };
                disputes.push(dispute);
            }
            resolveScorecardDispute(disputes);
        }
    };
    const resolveScorecardDispute = (disputes: any) => {
        const data = {
            disputes,
        };
        ResolveDispute(data)
        .unwrap()
        .then(() => {
            setSaveSuccessMessage('Dispute Resolved Successfully');
            window.localStorage.unCheckIt = true;
        })
        .then((error: any) => {
            console.log(error);
        });
    };
    const disputeModalFooter = () => {
        return (
            <React.Fragment>
                <Divider />
                <Button
                    className="btn-primary w-auto float-end"
                    style={{ fontSize: '14px', borderRadius: '8px' }}
                    onClick={saveDispute}
                    hidden={saveSuccessMessage !== ''}
                    disabled={!enableSaveButton}
                >
                    Save Changes
                </Button>
            </React.Fragment>
        );
    };
    useEffect(() => {
        if (disableDisputeDetails === true) {
            if (resolutionReason === '' || resolutionCommentText.trim() === '') {
                setEnableSaveButton(false);
            } else {
                setEnableSaveButton(true);
            }
        } else if (disableDisputeDetails === false) {
            if (disputeReason === 0 || disputeComments.trim() === '' || disputeDate === null) {
                setEnableSaveButton(false);
            } else setEnableSaveButton(true);
        }
    }, [disputeReason, disputeComments, disputeDate, resolutionReason, resolutionCommentText]);

    return (
        <Dialog
            visible={disputeDialog}
            style={{ width: '50vw' }}
            header={disputeModalHeader}
            modal
            className="p-fluid disputeModal"
            onHide={hideDisputeDialog}
            footer={disputeModalFooter}
        >
            <div className="row">
                <Divider className="m-0 pb-4" />
                {saveSuccessMessage === '' ? (
                    <div className="col-md-11 pe-0" style={{ width: '95.6%' }}>
                        <Button className="p-button text-center w-100 borderRadius" label="Dispute 1" />
                        {selectedDisputesList?.length === 1 || !disableDisputeDetails ? (
                            <div className="row pt-3">
                                <div className="col-md-6 pt-1">
                                    <div className="row">
                                        <div className="col-md-10">
                                            <h6 className="mt-1">Dispute Reason</h6>
                                            <Dropdown
                                                name="disputeReasonid"
                                                value={disputeReason}
                                                options={disputeReasonData?.data}
                                                onChange={(event) => setDisputeReason(event.target.value)}
                                                optionValue={'reason_id'}
                                                optionLabel={'reason_name'}
                                                placeholder="Select Reason"
                                                className="custom-dropdown"
                                                disabled={disableDisputeDetails}
                                            />
                                            <div>
                                                <h6 className="mt-3 pt-2">Date of Dispute</h6>
                                                <Calendar
                                                    id="basic"
                                                    value={disputeDate}
                                                    onChange={DateOnChange}
                                                    showIcon
                                                    className="custom-calendar"
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                    disabled={disableDisputeDetails}
                                                    data-testid="date-dispute"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <DescriptionBox
                                        title="Comment"
                                        value={disputeComments}
                                        responseDescription={disputeComments}
                                        onChange={(value) => {
                                            setDisputeComment(value);
                                        }}
                                        limit={4000}
                                        readOnly={disableDisputeDetails}
                                    />
                                    <div className="row disputeCommentFooterText">
                                        <div className="col-md-6">
                                            {disputeCreateDate.toLocaleString('en-US', {
                                                dateStyle: 'medium',
                                            })}
                                        </div>
                                        <div className="col-md-6 text-end">Added by {disputeCreatedBy}</div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div />
                        )}
                        {disableDisputeDetails === true ? (
                            <div className="row pt-3">
                                <div className="col-md-6 pt-1">
                                    <div className="row">
                                        <div className="col-md-10">
                                            <h6 className="mt-3 pt-2">Resolution Reason</h6>
                                            <Dropdown
                                                value={resolutionReason}
                                                options={resolutionReasons?.data}
                                                onChange={(e) => setResolutionReason(e.target.value)}
                                                optionLabel="reason_name"
                                                optionValue="reason_id"
                                                placeholder="Resolution select Reason"
                                                className="custom-dropdown"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <DescriptionBox
                                        title="Resolution Comment"
                                        value={resolutionCommentText}
                                        responseDescription={resolutionCommentText}
                                        onChange={(value) => {
                                            setResolutionCommentText(value);
                                        }}
                                        limit={4000}
                                    />
                                    <div className="row disputeCommentFooterText">
                                        <div className="col-md-6">
                                            {new Date().toLocaleString('en-US', { dateStyle: 'medium' })}
                                        </div>
                                        <div className="col-md-6 text-end">Added by {loggedUser}</div>
                                        <div className="col-md-12">
                                            * Comments appended to all selected disputes.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div />
                        )}
                    </div>
                ) : (
                    <div>{saveSuccessMessage}</div>
                )}
            </div>
        </Dialog>
    );
};
